// #328 — live alerts reach every open dashboard. Pure helpers (no React) for the live connection: the state
// the header shows, the newest event this tab announced (so a reconnect can replay what it missed), and the
// diagnostics trail. Storage can be missing or throw (private mode, blocked site data): every read and write
// is guarded and the feature simply does less.

/** Where the live connection stands. live = the server said connection_established. */
export type LiveStatus = 'connecting' | 'live' | 'reconnecting' | 'offline'

/** What the header shows: the first connection of the page looks like a reconnect. */
export function liveStatusView(status: LiveStatus): 'live' | 'reconnecting' | 'offline' {
  return status === 'connecting' ? 'reconnecting' : status
}

/** At most this many missed events get their own toast after a reconnect; the rest go into one summary toast. */
export const REPLAY_TOAST_CAP = 5

const LAST_ANNOUNCED_KEY = 'detercam-live-last'
const DEBUG_KEY = 'detercam-debug'

/** The newest event this tab announced. occurred_at is null for a live message (the handler sends none). */
export interface Announced {
  id: number
  occurred_at: string | null
}

export function readAnnounced(): Announced | null {
  try {
    const raw = sessionStorage.getItem(LAST_ANNOUNCED_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as { id?: unknown; occurred_at?: unknown } | null
    if (typeof value?.id !== 'number') return null
    return { id: value.id, occurred_at: typeof value.occurred_at === 'string' ? value.occurred_at : null }
  } catch {
    return null
  }
}

/** Remember an announced event if it is newer (higher id) than the one remembered: live messages can arrive
 *  out of order, and the replay needs the newest. */
export function rememberAnnounced(event: { id?: unknown; occurred_at?: unknown } | null | undefined): void {
  const id = event?.id
  if (typeof id !== 'number') return
  const last = readAnnounced()
  if (last && last.id >= id) return
  const occurred_at = typeof event?.occurred_at === 'string' ? event.occurred_at : null
  try {
    sessionStorage.setItem(LAST_ANNOUNCED_KEY, JSON.stringify({ id, occurred_at }))
  } catch { /* storage blocked: the next reconnect replays nothing */ }
}

/** #328 — a new page (or sign-in) starts its own marker: one left by an earlier page is not used for a replay. */
export function forgetAnnounced(): void {
  try {
    sessionStorage.removeItem(LAST_ANNOUNCED_KEY)
  } catch { /* storage blocked: nothing was stored either */ }
}

/** The events after the remembered one (id above it), oldest first (occurred_at, then id). */
export function pickMissed<T extends { id: number; occurred_at?: string | null }>(events: T[], last: Announced): T[] {
  const time = (e: T) => Date.parse(e.occurred_at ?? '')
  return events
    .filter((e) => e.id > last.id)
    .sort((a, b) => (time(a) - time(b)) || (a.id - b.id))
}

/** #328 — when the access token expires (ms since epoch), from the JWT payload's exp; null when it has none or
 *  cannot be read. Reading only: nothing here trusts the token, the server still checks it. */
export function tokenExpiresAt(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const exp = (JSON.parse(atob(padded)) as { exp?: unknown } | null)?.exp
    return typeof exp === 'number' ? exp * 1000 : null
  } catch {
    return null
  }
}

/** #328 — refresh before connecting: the token has no readable exp, has expired, or expires within marginMs.
 *  The server checks the token only when a socket connects and refuses an expired one. */
export function needsTokenRefresh(token: string, nowMs: number, marginMs: number): boolean {
  const expiresAt = tokenExpiresAt(token)
  return expiresAt === null || expiresAt - nowMs <= marginMs
}

export type LiveLogKind =
  | 'open' | 'close' | 'pong-timeout' | 'reconnect' | 'auth-refresh' | 'auth-refresh-failed' | 'replay' | 'message' | 'deaf-socket'
  | 'event-updated'  // #332 — a video's playable copy is ready or failed (a cache patch, not an announcement)
  | 'build'  // #336 — the new-build check failed (version.json missing or unreadable)

/** The diagnostics trail: one console line per connection event and per live message, only when
 *  localStorage['detercam-debug'] is '1'. Event ids only, nothing personal. */
export function liveLog(kind: LiveLogKind, data: Record<string, unknown> = {}): void {
  try {
    if (localStorage.getItem(DEBUG_KEY) !== '1') return
  } catch {
    return
  }
  console.info('[live]', kind, data)
}
